import React from "react";
import {
  Flex,
  Box,
  Stack,
  Button,
  Text,
  Input,
  Select,
  useToast,
  FormLabel,
  HStack,
  IconButton,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { useQuery } from "@apollo/client";
import moment from "moment";

import {
  INVITE_CANDIDATE_MUTATION,
  UPLOAD_EXTERNAL_CANDIDATE_RESUME_MUTATION,
  UPLOAD_EXTERNAL_CANDIDATE_CERTIFICATION_MUTATION,
} from "../graphql/agencies";
import { GET_CERTIFICATIONS_TYPE } from "../graphql/jobs";
import MoblyzeApolloClient from "../services/MoblyzeApolloClient";
import { ResumeFilePicker } from "./Filepicker";

const emptyCertRow = () => ({
  certificationId: "",
  name: "",
  issueDate: "",
  expiryDate: "",
  file: null,
});

export default function UpdateCandidateProfileForm({ currentUser }) {
  const toast = useToast();

  const [bullhornId, setBullhornId] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [primaryPhone, setPrimaryPhone] = React.useState("");

  const [resumeFile, setResumeFile] = React.useState(null);
  const [certRows, setCertRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const { data: certificationsData } = useQuery(GET_CERTIFICATIONS_TYPE);
  const certificationTypes = certificationsData?.getCertifications || [];

  const updateCertRow = (index, patch) => {
    setCertRows((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addCertRow = () => setCertRows((rows) => [...rows, emptyCertRow()]);
  const removeCertRow = (index) =>
    setCertRows((rows) => rows.filter((_, i) => i !== index));

  const uploadResume = async (externalCandidateId) => {
    if (!resumeFile) {
      return;
    }

    const r = await MoblyzeApolloClient.mutate({
      mutation: UPLOAD_EXTERNAL_CANDIDATE_RESUME_MUTATION,
      variables: { externalCandidateId, resumeFile },
    });
    console.log("Resume upload response", r);
  };

  const uploadCertificationFiles = async (externalCandidateId, createdCerts) => {
    for (let i = 0; i < certRows.length; i++) {
      const row = certRows[i];
      const createdCert = createdCerts?.[i];
      if (!createdCert?.id || !row.file) continue;

      await MoblyzeApolloClient.mutate({
        mutation: UPLOAD_EXTERNAL_CANDIDATE_CERTIFICATION_MUTATION,
        variables: {
          externalCandidateId,
          certificationId: createdCert.id,
          file: row.file,
        },
      });
    }
  };

  const validateRequiredFields = () => {
    if (!primaryPhone.trim()) {
      return "A phone number is required (e.g. +14152231900).";
    }
    return null;
  };

  const validateCertRows = () => {
    for (const row of certRows) {
      if (!row.certificationId) {
        return "Each certification must have a type.";
      }
      if (!row.issueDate) {
        return "Each certification must have an issue date.";
      }
      if (!row.file) {
        return "Each certification must have a file attached.";
      }
    }
    return null;
  };

  const onSaveButtonPress = async () => {
    const fieldError = validateRequiredFields();
    if (fieldError) {
      toast({ title: fieldError, status: "error" });
      return;
    }

    const certError = validateCertRows();
    if (certError) {
      toast({ title: certError, status: "error" });
      return;
    }

    setIsLoading(true);

    try {
      const certificationVariables = certRows.map((row) => {
        const type = certificationTypes.find((t) => t.id === row.certificationId);
        return {
          certificationId: row.certificationId,
          name: type?.name || "",
          issue: row.issueDate ? moment(row.issueDate).format() : null,
          expiry: row.expiryDate ? moment(row.expiryDate).format() : null,
        };
      });

      const r = await MoblyzeApolloClient.mutate({
        mutation: INVITE_CANDIDATE_MUTATION,
        variables: {
          bullhornId,
          firstName,
          lastName,
          email,
          phone: primaryPhone.trim(),
          certification: certificationVariables,
        },
      });
      console.log("Candidate invited response", r);

      const externalCandidateId = r?.data?.inviteCandidate?.id;
      const createdCerts = r?.data?.inviteCandidate?.certification || [];

      await uploadResume(externalCandidateId);
      await uploadCertificationFiles(externalCandidateId, createdCerts);

      toast({
        title: "Candidate invited and notified!",
        status: "success",
      });
    } catch (e) {
      console.log("Update user error", e);
      toast({
        title: e?.message || JSON.stringify(e),
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box borderRadius="5px" background="gray.800" p="44px 20px" width="100%">
      <Text
        color={"white"}
        fontSize={"28px"}
        fontWeight={600}
        lineHeight={"40px"}
      >
        Invite Candidate
      </Text>
      <Stack spacing={4} mt="8" width="100%">
        {currentUser?.agency?.type !== 'corporate' && (
          <>
            <FormLabel fontSize="xs" mb={0} color={"colors.white"}>
              Bullhorn ID
            </FormLabel>
            <Input
              mt="0px"
              placeholder="Bullhorn ID"
              label="Bullhorn ID"
              value={bullhornId}
              onChange={(e) => setBullhornId(e.target.value)}
            />
          </>
        )}

        <FormLabel fontSize="xs" mb={0} color={"colors.white"}>
          First Name
        </FormLabel>
        <Input
          mt="0px"
          placeholder="First name"
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <FormLabel fontSize="xs" mb={0} color={"colors.white"}>
          Last Name
        </FormLabel>
        <Input
          mt="0px"
          placeholder="Last name"
          label="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <FormLabel fontSize="xs" mb={0} color={"colors.white"}>
          Email
        </FormLabel>
        <Input
          mt="0px"
          placeholder="Email"
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <FormLabel fontSize="xs" mb={0} color={"colors.white"}>
          Phone (required, e.g. +14152231900)
        </FormLabel>
        <Input
          mt="0px"
          placeholder="Phone"
          label="Phone"
          value={primaryPhone}
          isRequired
          onChange={(e) => setPrimaryPhone(e.target.value)}
        />

        <ResumeFilePicker name="resumeFile" setResumeValue={setResumeFile} />

        <Box>
          <FormLabel fontSize="xs" mb={2} color={"colors.white"}>
            Certifications
          </FormLabel>
          <Stack spacing={3}>
            {certRows.map((row, index) => (
              <Box
                key={index}
                p={3}
                borderRadius="md"
                background="gray.700"
              >
                <HStack justify="space-between" mb={2}>
                  <Text color="white" fontSize="sm" fontWeight={600}>
                    Certification {index + 1}
                  </Text>
                  <IconButton
                    aria-label="Remove certification"
                    icon={<CloseIcon boxSize={3} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => removeCertRow(index)}
                  />
                </HStack>
                <Stack spacing={2}>
                  <Select
                    placeholder="Select certification type"
                    value={row.certificationId}
                    onChange={(e) =>
                      updateCertRow(index, { certificationId: e.target.value })
                    }
                  >
                    {certificationTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </Select>
                  <HStack>
                    <Box flex={1}>
                      <FormLabel fontSize="xs" mb={0} color={"colors.white"}>
                        Issue Date
                      </FormLabel>
                      <Input
                        type="date"
                        aria-label={`Certification ${index + 1} issue date`}
                        value={row.issueDate}
                        onChange={(e) =>
                          updateCertRow(index, { issueDate: e.target.value })
                        }
                      />
                    </Box>
                    <Box flex={1}>
                      <FormLabel fontSize="xs" mb={0} color={"colors.white"}>
                        Expiry Date
                      </FormLabel>
                      <Input
                        type="date"
                        aria-label={`Certification ${index + 1} expiry date`}
                        value={row.expiryDate}
                        onChange={(e) =>
                          updateCertRow(index, { expiryDate: e.target.value })
                        }
                      />
                    </Box>
                  </HStack>
                  <Input
                    type="file"
                    aria-label={`Certification ${index + 1} file`}
                    p={1}
                    onChange={(e) =>
                      updateCertRow(index, { file: e.target.files?.[0] || null })
                    }
                  />
                </Stack>
              </Box>
            ))}
            <Button variant="outline" onClick={addCertRow}>
              Add certification
            </Button>
          </Stack>
        </Box>

        <Flex align="center">
          <Button
            width="100%"
            onClick={onSaveButtonPress}
            isLoading={isLoading}
            disabled={isLoading}
          >
            Send Candidate Invite
          </Button>
        </Flex>
      </Stack>
    </Box>
  );
}
